import React, { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiUser, PapiDeviation } from '@wix/da-papi-types';
import AddIcon from '@wix/da-ds/pkg/Icons/24x24/Add';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BrandedButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/BrandedButtonWithContextMenu';
import MediaSelectorWithGroupGallection from '@wix/da-shared-react/pkg/MediaPopup/Groups/MediaSelectorWithGroupGallection';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { GroupGallectionType } from '@wix/da-shared-react/pkg/GroupGallectionSelector/redux/types';
import { showSubmitModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { getSubmitUrlForProfileOwner } from '../../../../../../../utils/urls';

export interface Props {
  className?: string;
  profileOwnerUser: PapiUser;
  contributeDeviation: (
    deviation: PapiDeviation,
    group: PapiUser,
    type: GroupGallectionType,
    folderId?: number
  ) => void;
  canContributeDeviations: boolean;
  canRecommendDeviations: boolean;
  canSubmitJournal: boolean;
}

const SubmitButton: React.FC<Props> = ({
  className,
  profileOwnerUser,
  contributeDeviation,
  canContributeDeviations,
  canRecommendDeviations,
  canSubmitJournal,
}) => {
  const { t } = useTranslation();
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const dispatch = useDispatch();
  const isMobile = useContext(MobileContext);

  if (!canContributeDeviations && !canRecommendDeviations) {
    return null;
  }

  const openMediaSelector = () => {
    setIsMediaSelectorOpen(true);
  };

  const closeMediaSelector = () => {
    setIsMediaSelectorOpen(false);
  };

  const redirectToSubmitPage = () => {
    window.location.replace(getSubmitUrlForProfileOwner(profileOwnerUser));
  };

  const submitJournal = () => {
    dispatch(
      showSubmitModal(ModalType.JOURNAL, {
        params: {
          refreshAfterSubmit: true,
          groupDestination: profileOwnerUser,
        },
      })
    );
  };
  const submitStatusUpdate = () => {
    dispatch(
      showSubmitModal(ModalType.STATUS_UPDATE, {
        params: { refreshAfterSubmit: true },
      })
    );
  };

  let items = [] as any;

  if (canContributeDeviations) {
    items.push(
      {
        label: t('groups.submit.contributeNewDeviation'),
        onClick: redirectToSubmitPage,
      },
      {
        label: t('groups.submit.contributeExistingDeviation'),
        onClick: openMediaSelector,
      }
    );
  } else if (canRecommendDeviations) {
    items.push({
      label: t('groups.submit.recommendExistingDeviation'),
      onClick: openMediaSelector,
    });
  }

  if (!isMobile && canSubmitJournal) {
    items.push({
      label: t('groups.submit.writePost'),
      onClick: profileOwnerUser.isGroup ? submitJournal : submitStatusUpdate,
    });
  }

  items = items.filter(x => x);

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <BrandedButtonWithContextMenu
        icon={AddIcon}
        items={items}
        size="large"
        variant="brand-green"
        className={className}
      >
        {t('common.submit')}
      </BrandedButtonWithContextMenu>
      {isMediaSelectorOpen && (
        <MediaSelectorWithGroupGallection
          group={profileOwnerUser}
          onClose={closeMediaSelector}
          allowUploads={false}
          mediaSourceTypes={[
            MediaSourceType.GALLERY,
            MediaSourceType.COLLECTION,
            MediaSourceType.JOURNAL,
            MediaSourceType.STATUS,
            MediaSourceType.BROWSE,
          ]}
          onSelect={(...args) => {
            contributeDeviation(...args);
            setIsMediaSelectorOpen(false);
          }}
        />
      )}
    </>
  );
};
SubmitButton.displayName = 'SubmitButton';

export default SubmitButton;
